import { useContext, useEffect } from 'react';
import devLogger from 'react-utils/devLogger';
import { useApolloClient } from '@apollo/client';
import Raven from 'raven-js';
import { Metrics } from './Metrics';
import { EDIT_INPUT_MODE } from '../v2/types/PropertyInputV2Component';
import { getDataFetchingClientContext } from 'data-fetching-client';
import { isDateTimeWithHiddenTime } from 'customer-data-objects/property/PropertyIdentifier';
import { normalizeProperty } from '../utils/normalizeProperty';
const useApolloClientCanary = ({
  objectTypeId,
  property
}) => {
  // Calling `useApolloClient` outside of an `ApolloProvider` context is dangerous because it will throw:
  // ```
  // Uncaught Invariant Violation: An error occurred! For more details, see the full error text at https://go.apollo.dev/c/err#%7B%22version%22%3A%223.11.4%22%2C%22message%22%3A50%2C%22args%22%3A%5B%5D%7D
  // ```
  let apolloClient = undefined;
  try {
    // Dear React: it's okay to call this inside a try/catch, because _calling it can throw_ and we need to catch it
    // eslint-disable-next-line react-hooks/rules-of-hooks
    apolloClient = useApolloClient();
  } catch (error) {
    // Do nothing; the canary below will log the failure
  }
  useEffect(() => {
    if (!apolloClient) {
      devLogger.warn({
        message: `propertyinputv2-apollo-client-canary: rendered without an <ApolloProvider>: objectTypeId=${objectTypeId}, propertyName=${property.name}`,
        key: 'propertyinputv2-apollo-client-canary'
      });
      Metrics.counter('propertyinputv2-apollo-client-canary').increment();
      // @gmcnaughton Sep 16, 2024 - disabled the Sentry until we can fix SalesSidebar
      // (gmail/outlook extension), which is a known omission. they were getting
      // paged due to the quantity of errors logged.
      // Raven.captureException(
      //   new Error('propertyinputv2 apollo client canary'),
      //   {
      //     extra: {
      //       objectTypeId,
      //       propertyName: property.name,
      //     },
      //   }
      // );
    }
  }, [apolloClient, objectTypeId, property.name]);
};
const useDataFetchingProviderCanary = () => {
  const {
    client
  } = useContext(getDataFetchingClientContext());
  useEffect(() => {
    if (!client) {
      devLogger.warn({
        message: 'propertyinputv2-data-fetching-provider-canary',
        key: 'propertyinputv2-data-fetching-provider-canary'
      });
      Metrics.counter('propertyinputv2-data-fetching-provider-canary').increment();
    }
  }, [client]);
};
const useDataSensitivityCanary = ({
  objectTypeId,
  property
}) => {
  useEffect(() => {
    if (property.dataSensitivity === undefined) {
      devLogger.warn({
        message: `propertyinputv2-data-sensitivity-canary: received a property without dataSensitivity: objectTypeId=${objectTypeId}, propertyName=${property.name}`,
        key: 'propertyinputv2-data-sensitivity-canary'
      });
      Metrics.counter('propertyinputv2-data-sensitivity-canary').increment();
      // sam swindell July 15, 2024 - turned off to stop the flood until we have
      // fixed some known cases. Worried about COGS when sending too many sentries!
      // Raven.captureException(
      //   new Error('propertyinputv2 dataSensitivity canary'),
      //   {
      //     extra: {
      //       objectTypeId,
      //       propertyName: property.name,
      //     },
      //   }
      // );
    }
  }, [objectTypeId, property.dataSensitivity, property.name]);
};
const useObjectIdCanary = ({
  objectTypeId,
  objectId,
  inputMode = EDIT_INPUT_MODE,
  property
}) => {
  useEffect(() => {
    if (inputMode === EDIT_INPUT_MODE && !objectId) {
      devLogger.warn({
        message: `propertyinputv2-objectid-canary: received an invalid objectId/inputMode (objectId is required when inputMode = "edit"): objectTypeId=${objectTypeId}, objectId=${objectId}, inputMode=${inputMode}, propertyName=${property.name}`,
        key: 'propertyinputv2-objectid-canary'
      });
      Metrics.counter('propertyinputv2-objectid-canary').increment();
      Raven.capturePageEvent('propertyinputv2 objectid canary', {
        extra: {
          objectTypeId,
          objectId,
          inputMode,
          propertyName: property.name
        }
      });
    } else if (inputMode !== EDIT_INPUT_MODE && objectId) {
      devLogger.warn({
        message: `propertyinputv2-objectid-nonedit-canary: received an invalid objectId/inputMode (objectId is not expected when inputMode != "edit"): objectTypeId=${objectTypeId}, objectId=${objectId}, inputMode=${inputMode}, propertyName=${property.name}`,
        key: 'propertyinputv2-objectid-nonedit-canary'
      });
      Metrics.counter('propertyinputv2-objectid-nonedit-canary').increment();
      Raven.capturePageEvent('propertyinputv2 objectid nonedit canary', {
        extra: {
          objectTypeId,
          objectId,
          inputMode,
          propertyName: property.name
        }
      });
    }
  }, [objectTypeId, objectId, inputMode, property.name]);
};
const useProductMetricsCanary = ({
  inputMode,
  use
}) => {
  useEffect(() => {
    if (inputMode) {
      Metrics.counter('propertyinputv2-inputmode', {
        mode: inputMode
      }).increment();
    }
    if (use) {
      Metrics.counter('propertyinputv2-use', {
        use
      }).increment();
    }

    // Intentionally only run once on mount to avoid noise and poorly memoized apps from
    // skewing these metrics too heavily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
const useInputPropsCanary = ({
  inputProps
}) => {
  useEffect(() => {
    if (inputProps && Object.prototype.hasOwnProperty.call(inputProps, 'isRequired')) {
      Metrics.counter('propertyinputv2-inputProps-isRequired').increment();
    }
  }, [inputProps]);
};
const usePopoverPropsCanary = ({
  popoverProps
}) => {
  useEffect(() => {
    if (popoverProps) {
      Metrics.counter('propertyinputv2-popoverProps').increment();
    }
  }, [popoverProps]);
};
const useDateTimeWithHiddenTimeCanary = ({
  property,
  objectTypeId
}) => {
  useEffect(() => {
    const normalizedProperty = normalizeProperty(property);
    if (isDateTimeWithHiddenTime(normalizedProperty, objectTypeId)) {
      Metrics.counter('propertyinputv2-dateTimeWithHiddenTime', {
        objectTypeId,
        propertyName: property.name
      }).increment();
    }
  }, [property, objectTypeId]);
};
export const usePropertyInputCanaryMetrics = props => {
  useDataFetchingProviderCanary();
  useApolloClientCanary(props);
  useDataSensitivityCanary(props);
  useObjectIdCanary(props);
  useProductMetricsCanary(props);
  useInputPropsCanary(props);
  usePopoverPropsCanary(props);
  useDateTimeWithHiddenTimeCanary(props);
};